<script setup>
import { ref, onMounted } from 'vue';
import { Cookie } from 'lucide-vue-next';
import { grantAdsConsent, denyAdsConsent, initMetaPixel } from '../utils/metaPixel';
import { ANALYTICS_CONSENT_KEY, notifyAnalyticsConsent } from '../utils/consent';

// Composant web uniquement : App.vue ne le monte pas en natif.
const show = ref(false);

/** Aligne le Consent Mode v2 de Google sur le choix de l'utilisateur */
const applyGoogleConsent = (granted) => {
    if (typeof window.gtag !== 'function') return;
    const state = granted ? 'granted' : 'denied';
    window.gtag('consent', 'update', {
        'analytics_storage': granted ? 'granted' : 'denied',
        'ad_storage': state,
        'ad_user_data': state,
        'ad_personalization': state,
    });
};

const accept = () => {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, 'accepted');
    show.value = false;
    applyGoogleConsent(true);
    // Charge gtag.js et Firebase Analytics, differes tant que rien n'etait accepte
    notifyAnalyticsConsent();
    // Autorise et démarre le pixel Meta (mesure des campagnes Instagram)
    grantAdsConsent();
};

const refuse = () => {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, 'refused');
    show.value = false;
    applyGoogleConsent(false);
    denyAdsConsent();
};

onMounted(() => {
    const choice = localStorage.getItem(ANALYTICS_CONSENT_KEY);

    if (choice === 'accepted') {
        applyGoogleConsent(true);
        initMetaPixel();
    } else if (choice === 'refused') {
        applyGoogleConsent(false);
    } else {
        // Petit délai pour ne pas agresser l'utilisateur dès l'arrivée
        setTimeout(() => {
            show.value = true;
        }, 1000);
    }
});
</script>

<template>
    <Transition
        enter-active-class="transform ease-out duration-500 transition-all"
        enter-from-class="translate-y-full opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transform ease-in duration-300 transition-all"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-full opacity-0"
    >
        <!-- z-[80] et décalage vertical : la barre de navigation mobile est en z-[70] et
             occupe le bas de l'écran, la bannière doit passer au-dessus, pas dessous. -->
        <div v-if="show" class="fixed bottom-[calc(5.25rem+env(safe-area-inset-bottom))] md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[80]">
            <div class="bg-white border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-4 rounded-2xl flex flex-col gap-3">

                <div class="flex gap-3 items-start">
                    <div class="p-2 bg-pink-50 text-[#e94591] rounded-full shrink-0">
                        <Cookie class="w-5 h-5" />
                    </div>
                    <p class="text-xs text-gray-600 leading-relaxed font-medium">
                        Cookies essentiels pour le fonctionnement (paiement, carte). Avec ton accord,
                        on mesure aussi l'audience et l'efficacité de nos publicités.
                    </p>
                </div>

                <div class="flex gap-2 w-full">
                    <button
                        @click="refuse"
                        class="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-4 py-2.5 rounded-xl transition-colors active:scale-95"
                    >
                        Refuser
                    </button>
                    <button
                        @click="accept"
                        class="flex-1 bg-gray-900 hover:bg-black text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-colors shadow-sm active:scale-95"
                    >
                        Tout accepter
                    </button>
                </div>

            </div>
        </div>
    </Transition>
</template>
